import { faqsPl } from "@data/prototyping-landing/faq";
import {
  Battery,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  FlaskConical,
  Headphones,
  LifeBuoy,
  MapPin,
  PenTool,
  Radio,
  Rocket,
  Search,
  ShieldCheck,
  Smartphone,
  Users,
  Watch,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title = "Prototypypowanie elektroniki Bluetooth LE i Auracast";
export const description =
  "Jesteśmy firmą prototypującą urządzenia Bluetooth Low Energy - wearable, beacony iBeacon i produkty audio zgodne z Bluetooth LE Audio (Auracast). Od pomysłu po działający prototyp gotowy do testów i dalszej produkcji.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Prototypowanie urządzeń Bluetooth Low Energy",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "Hardware and firmware prototyping",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsPl.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const heroCopy: HeroCopy = {
  header: "Prototypowanie",
  headerTail: "Bluetooth\xa0LE i Auracast",
  description:
    "Budujemy działające prototypy urządzeń Bluetooth\xa0Low\xa0Energy - opasek i sensorów wearable, beaconów iBeacon oraz produktów zgodnych z Auracast Bluetooth\xa0LE\xa0Audio. Od pierwszego PoC po plan wejścia w produkcję.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "Prototyp\xa0w\xa0kilka\xa0tygodni", Icon: CheckCircle },
    {
      text: "Elektronika, firmware i aplikacja mobilna",
      Icon: CheckCircle,
    },
    { text: "Gotowe na dalszy rozwój", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest ta oferta?",
    p: "Pracujemy z firmami i startupami, które chcą sprawdzić pomysł na urządzenie BLE, zanim zainwestują w produkcję seryjną. Czy któryś z tych punktów pasuje do Twojej sytuacji?",
  },
  points: [
    {
      Icon: Watch,
      title: "Budujesz produkt wearable",
      description:
        "Opaska, smartwatch albo sensor do noszenia na ciele - potrzebujesz prototypu, który realnie działa na baterii i wytrzymuje codzienne użytkowanie.",
    },
    {
      Icon: Radio,
      title: "Chcesz wdrożyć beacony w swoim biznesie",
      description:
        "Nawigacja w budynku, marketing kontekstowy albo śledzenie zasobów - potrzebujesz prototypu sieci beaconów iBeacon lub Eddystone, zanim zamówisz setki urządzeń.",
    },
    {
      Icon: Headphones,
      title: "Projektujesz produkt audio z Bluetooth LE Audio",
      description:
        "Aparaty słuchowe, systemy nagłośnieniowe albo słuchawki z funkcją Auracast - potrzebujesz prototypu zgodnego z nowym standardem LE Audio.",
    },
    {
      Icon: FlaskConical,
      title: "Masz pomysł, ale nie masz jeszcze urządzenia",
      description:
        "Zaczynasz od zera - potrzebujesz partnera, który pomoże dobrać chip, zbuduje pierwszy prototyp i zweryfikuje pomysł z realnymi użytkownikami.",
    },
    {
      Icon: Rocket,
      title: "Masz działający prototyp i szukasz drogi do produkcji",
      description:
        "Twój prototyp działa na płytce deweloperskiej, ale potrzebujesz pomocy w przejściu do własnego PCB, obudowy i certyfikacji przed produkcją seryjną.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o swoim pomyśle na urządzenie BLE. Pomożemy zaplanować ścieżkę od koncepcji do działającego prototypu.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto zbudować prototyp właśnie z nami?",
    p: "Łączymy elektronikę, firmware i aplikację mobilną w jednym zespole. Nie musisz szukać osobno inżyniera hardware, programisty firmware i dewelopera aplikacji - budujemy kompletny prototyp od podstaw.",
    imageAlt: "Prototypowanie urządzenia wearable z Bluetooth Low Energy",
  },
  values: [
    {
      Icon: Cpu,
      title: "Szybkie prototypowanie na sprawdzonym sprzęcie",
      description:
        "Korzystamy z płytek deweloperskich Thingy od Nordic Semiconductors i sprawdzonych modułów, dzięki czemu pierwszy działający prototyp powstaje w tygodnie, nie miesiące.",
    },
    {
      Icon: Battery,
      title: "Długi czas pracy na baterii",
      description:
        "Wiemy, jak zaprojektować firmware i sprzęt tak, żeby urządzenie mogło pracować na baterii miesiącami, a nie tylko dniami - dobieramy tryby uśpienia, interwały rozgłaszania i komponenty pod kątem realnego poboru prądu.",
    },
    {
      Icon: Radio,
      title: "Beacony iBeacon i Eddystone od podszewki",
      description:
        "Konfigurujemy rozgłaszanie, advertising intervals i moc sygnału tak, żeby beacony działały niezawodnie w realnych warunkach - w sklepie, magazynie czy muzeum.",
    },
    {
      Icon: Headphones,
      title: "Doświadczenie z Bluetooth LE Audio i Auracast",
      description:
        "Śledzimy rozwój standardu LE Audio i pracujemy z nRF5340 oraz nRF54H20, żeby prototypować produkty audio gotowe na Auracast, zanim stanie się on standardem rynkowym.",
    },
    {
      Icon: Rocket,
      title: "Prototyp z myślą o produkcji",
      description:
        "Budujemy prototypy tak, żeby dało się je świadomie rozwinąć do produkcji seryjnej - z uwzględnieniem kosztów komponentów, certyfikacji CE i wymagań Bluetooth SIG.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Prototypowanie urządzeń Bluetooth Low Energy",
    p: "Od pierwszej koncepcji po działający prototyp gotowy do testów z użytkownikami. Zajmujemy się elektroniką, firmware i aplikacją mobilną.",
  },
  services: [
    {
      Icon: Search,
      title: "Analiza koncepcji i dobór komponentów",
      description:
        "Analizujemy Twój pomysł, dobieramy odpowiedni chip BLE, czujniki i komponenty oraz szacujemy zużycie energii i koszt jednostkowy jeszcze przed budową prototypu.",
    },
    {
      Icon: Cpu,
      title: "Prototypowanie elektroniki",
      description:
        "Budujemy prototyp na płytce deweloperskiej lub własnym PCB, integrujemy czujniki, baterię i obudowę testową gotową do noszenia lub montażu.",
    },
    {
      Icon: Code,
      title: "Firmware i profile BLE",
      description:
        "Piszemy firmware obsługujący rozgłaszanie, parowanie i profile GATT lub broadcast - dopasowane do wearable, beaconów albo LE Audio.",
    },
    {
      Icon: Watch,
      title: "Ergonomia i czas pracy na baterii",
      description:
        "Testujemy i optymalizujemy pobór energii, dobieramy tryby uśpienia i interwały rozgłaszania, żeby prototyp realnie działał na baterii tak długo, jak potrzebujesz.",
    },
    {
      Icon: Smartphone,
      title: "Aplikacja mobilna do prototypu",
      description:
        "Budujemy aplikację towarzyszącą, która pozwala testować urządzenie z użytkownikami - podgląd danych, konfigurację i aktualizacje firmware.",
    },
    {
      Icon: FlaskConical,
      title: "Testujemy każdy projekt",
      description:
        "Testy integracyjne firmware weryfikują rozgłaszanie, parowanie i transmisję danych - kolejne wersje nigdy nie psują tego, co już działa.",
    },
    {
      Icon: Rocket,
      title: "Plan wejścia w produkcję",
      description:
        "Przygotowujemy plan przejścia od prototypu do produkcji seryjnej - wybór podwykonawcy PCB, certyfikacja CE i Bluetooth SIG, dokumentacja produkcyjna.",
    },
    {
      Icon: LifeBuoy,
      title: "Wsparcie po zakończeniu prototypu",
      description:
        "Pomagamy iterować kolejne wersje prototypu, dodawać nowe funkcje i przygotować urządzenie do kolejnych rund testów lub inwestycji.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Opisz nam swój pomysł na urządzenie - nawet jeśli masz tylko szkic na kartce. Podpowiemy, jak zaplanować pierwszy prototyp.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak wygląda projekt prototypu",
    p: "Przejrzyste etapy, szybkie iteracje i realny sprzęt w rękach już po kilku tygodniach. Zobacz, jak przechodzimy od pomysłu do działającego prototypu.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy z inżynierem. Opisujesz pomysł na urządzenie, a my dajemy pierwszą ocenę wykonalności i orientacyjną wycenę.",
    },
    {
      Icon: FileText,
      title: "Warsztaty koncepcyjne",
      duration: "w kilka dni",
      description:
        "Po 2-godzinnej sesji online otrzymujesz raport z doborem komponentów, szacowanym budżetem i harmonogramem budowy prototypu.",
    },
    {
      Icon: PenTool,
      title: "Podpisanie umowy",
      duration: "ustalenie harmonogramu",
      description:
        "Ustalamy zakres pierwszej iteracji prototypu, kamienie milowe i sposób komunikacji postępów.",
    },
    {
      Icon: Search,
      title: "Zakup komponentów",
      duration: "1-2 tygodnie",
      description:
        "Zamawiamy płytki deweloperskie, czujniki i moduły potrzebne do pierwszej wersji prototypu.",
    },
    {
      Icon: Code,
      title: "Budowa pierwszej iteracji",
      duration: "min. 3-6 tygodni",
      description:
        "Elektronika, firmware i aplikacja mobilna powstają równolegle, żeby jak najszybciej mieć działający prototyp w ręku.",
    },
    {
      Icon: FlaskConical,
      title: "Testy i iteracje",
      duration: "min. 2-4 tygodnie",
      description:
        "Testujemy prototyp w realnych warunkach, zbieramy wnioski i wprowadzamy poprawki w kolejnej iteracji.",
    },
    {
      Icon: Rocket,
      title: "Plan wejścia w produkcję",
      duration: "min. 2 tygodnie",
      description:
        "Przygotowujemy dokumentację i plan przejścia od prototypu do własnego PCB, obudowy i produkcji seryjnej.",
    },
    {
      Icon: CheckCircle,
      title: "Przekazanie i dalszy rozwój",
      duration: "na stałe",
      description:
        "Otrzymujesz działający prototyp, dokumentację i kod źródłowy, albo kontynuujemy współpracę przy kolejnych iteracjach i przygotowaniach do produkcji.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów prototyp urządzenia BLE",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "O nas",
    h3: "Zespół, który zbuduje Twój pierwszy prototyp",
    p1: "Jesteśmy małym zespołem z Polski, który łączy doświadczenie w elektronice, firmware Bluetooth Low Energy i aplikacjach mobilnych.",
    p2: "Michał i Tomasz odpowiadają za elektronikę i firmware, Kuba za backend i infrastrukturę, a Mateusz projektuje UX, który sprawia, że prototyp jest gotowy do pokazania użytkownikom i inwestorom.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół budujący prototyp urządzenia BLE",
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z inżynierami",
      description:
        "Bez pośredników - osoba na rozmowie to osoba, która lutuje i programuje Twój prototyp.",
    },
    {
      Icon: MapPin,
      title: "Rozumiemy wymagania europejskiego rynku",
      description:
        "Wiemy, jakie certyfikaty i wymagania czekają na urządzenie BLE wchodzące na rynek UE.",
    },
    {
      Icon: ShieldCheck,
      title: "Dbamy o bezpieczeństwo danych",
      description:
        "Wszystkie dane z testów i prototypów są przechowywane w UE pod okiem polskich specjalistów.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o prototypowanie urządzeń BLE",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Masz pomysł na urządzenie BLE?",
    p: "Umów się na darmową konsultację, opowiedz nam o swoim pomyśle i poznaj wstępny plan budowy prototypu.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Prototypujemy urządzenia Bluetooth Low Energy - wearable, beacony i produkty audio z Auracast. Zapewniamy kompleksowe wsparcie od koncepcji po plan wejścia w produkcję.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "/projects", text: "Projekty" },
      { href: "/blog", text: "Blog" },
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/aplikacje-bluetooth", text: "Aplikacje mobilne Bluetooth" },
      {
        href: "/programowanie-firmware-ble",
        text: "Programowanie firmware BLE",
      },
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [
      {
        href: "/pl/blogs/is_bluetooth_le_right_for_your_product",
        text: "Czy Bluetooth LE to właściwa technologia do realizacji Twojego produktu lub MVP?",
      },
      {
        href: "/pl/blogs/bluetooth_le_product_architecture",
        text: "Architektura produktu opartego o Bluetooth LE",
      },
      {
        href: "/pl/blogs/ble_mobile_application_support",
        text: "Wsparcie BLE po stronie aplikacji mobilnej",
      },
      {
        href: "/pl/blogs/ble_embedded_development_platforms",
        text: "Najlepsze platformy rozwojowe embedded z Bluetooth LE",
      },
      {
        href: "/pl/blogs/auracast_mcu",
        text: "ESP32 Auracast: czy działa? Najlepsze MCU do LE Audio",
      },
      {
        href: "/pl/blogs/ble_hardware_2026",
        text: "Najlepszy hardware do rozwoju Bluetooth Low Energy BLE w 2026 roku",
      },
    ],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: {
    heading: "Napisz do nas",
    fields: {
      name: {
        label: "Imię i nazwisko",
        placeholder: "Jan Kowalski",
        requiredError: "Podaj imię i nazwisko",
      },
      email: {
        label: "Adres e-mail",
        placeholder: "jan@firma.pl",
        requiredError: "Podaj adres e-mail",
        invalidError: "Podaj poprawny adres e-mail",
      },
      topic: {
        label: "Opis projektu",
        placeholder: "Krótko opisz swój pomysł na urządzenie BLE...",
        requiredError: "Opisz swój projekt",
        maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
      },
    },
    submit: "Wyślij zapytanie",
    submitting: "Wysyłanie...",
    successMessage: "Wiadomość wysłana!",
    errorMessage: "Nie udało się wysłać wiadomości :(",
    responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
    emailSubjectPrefix: "Imię i nazwisko",
  },
};
